import { ISiderMenu } from '@contexts/AuthProvider';
import { AuthService } from '@core/services/auth.service';
import React, { ComponentType } from 'react';
import { Redirect, useLocation } from 'react-router';

const trimSlash = (path: string): string => path.replace(/\/+$/, '');

/**
 * Hides a screen the user has no menu for.
 *
 * The routes carry no permission metadata, so until now typing an admin URL rendered the screen
 * for anyone - every server call was refused, but the shell still appeared with an empty table.
 * This checks the same menu list the sidebar is built from.
 *
 * Cosmetic only: the API is the real gate. It therefore fails OPEN whenever the menu list is not
 * yet known, so a slow or failed /users/menu can never lock out a legitimate user. A user without
 * the menu is sent to the home page.
 *
 * This is the FIRST of two layers, and the only one that can act before anything renders. It knows
 * only what the cached sidebar knows, so it cannot see a menu revoked mid-session, a stale
 * x-menu-id left in an old tab, or a route it has no wrapper on. Those surface as a refusal from
 * the API, and the axios response interceptor sends the user home on those - see isScreenNotYours
 * in @core/axios.
 *
 * Keep both. This one avoids the flash of an empty shell; that one catches everything this cannot
 * see. Deleting either leaves a real gap.
 */
export function withMenuAccess<P>(Component: ComponentType<P>): React.FC<P> {
  return function withMenuAccess(props: P): JSX.Element {
    const { pathname } = useLocation();

    const stored = AuthService.getSideMenus();
    const siderMenus: ISiderMenu | undefined = stored
      ? JSON.parse(stored)
      : undefined;
    const menus = siderMenus?.menus;

    // not loaded yet, or loaded but empty: fail open, the server still refuses
    if (!menus || menus.length === 0) {
      return <Component {...props} />;
    }

    const allowed = menus
      .flatMap((menu) => menu.subMenus || [])
      .some((subMenu) => trimSlash(subMenu.path) === trimSlash(pathname));

    // Home rather than /error/access: the user did not fail a check on a screen they were offered,
    // they typed a URL that was never theirs. Redirect replaces the history entry, so Back does not
    // drop them straight back onto it.
    return allowed ? <Component {...props} /> : <Redirect to="/" />;
  };
}
